<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import TableHeaderCell from 'dashboard/components/widgets/TableHeaderCell.vue';
import CheckBox from 'v3/components/Form/CheckBox.vue';
import {
  getDelfosDeviceId,
  getDelfosNotificationPreferences,
  hasPushPermissions,
  requestPushPermissions,
  unregisterDelfosPushSubscription,
  updateDelfosPushSubscription,
  verifyServiceWorkerExistence,
} from 'dashboard/helper/pushHelper.js';
import DelfosNotificationsAPI from 'dashboard/api/delfosNotifications';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import ToggleSwitch from 'dashboard/components-next/switch/Switch.vue';
import { NOTIFICATION_TYPES } from './constants';

export default {
  components: {
    TableHeaderCell,
    ToggleSwitch,
    CheckBox,
  },
  data() {
    return {
      selectedEmailFlags: [],
      selectedPushFlags: [],
      enableAudioAlerts: false,
      hasEnabledPushPermissions: false,
      notificationTypes: NOTIFICATION_TYPES,
      delfosPreferences: getDelfosNotificationPreferences(),
      delfosDevice: null,
      isSavingDelfos: false,
      isTestingDelfos: false,
      delfosCopy: {
        title: 'Avisos neste computador',
        sound: 'Som',
        preview: 'Foto e previa',
        internal: 'Grupos internos',
        reactivate: 'Reativar',
        deviceName: 'Nome deste computador',
        mute30: '30 min',
        muteHour: 'Silenciar 1 hora',
        muteDay: 'Ate o fim do dia',
        testing: 'Enviando...',
        test: 'Testar aviso',
        remove: 'Remover este computador',
      },
    };
  },
  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
      emailFlags: 'userNotificationSettings/getSelectedEmailFlags',
      pushFlags: 'userNotificationSettings/getSelectedPushFlags',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
    }),
    hasPushAPISupport() {
      return !!('Notification' in window);
    },
    isDelfosMuted() {
      return (
        this.delfosPreferences.mutedUntil &&
        new Date(this.delfosPreferences.mutedUntil).getTime() > Date.now()
      );
    },
    delfosStatusLabel() {
      if (!('Notification' in window)) return 'Indisponivel';
      if (Notification.permission === 'denied') return 'Bloqueado pelo Chrome';
      if (this.isDelfosMuted) return 'Silenciado';
      return this.delfosPreferences.enabled ? 'Ativo' : 'Desativado';
    },
    isSLAEnabled() {
      return this.isFeatureEnabledonAccount(this.accountId, FEATURE_FLAGS.SLA);
    },
    filteredNotificationTypes() {
      return this.notificationTypes.filter(notification =>
        this.isSLAEnabled
          ? true
          : ![
              'sla_missed_first_response',
              'sla_missed_next_response',
              'sla_missed_resolution',
            ].includes(notification.value)
      );
    },
  },
  watch: {
    emailFlags(value) {
      this.selectedEmailFlags = value;
    },
    pushFlags(value) {
      this.selectedPushFlags = value;
    },
  },
  mounted() {
    if (hasPushPermissions()) {
      this.getPushSubscription();
    }
    this.$store.dispatch('userNotificationSettings/get');
    this.refreshDelfosDevice();
  },
  methods: {
    async refreshDelfosDevice() {
      if (!this.accountId) return;
      try {
        const { data } = await DelfosNotificationsAPI.getConfiguration(
          this.accountId,
          getDelfosDeviceId()
        );
        this.delfosDevice = data.device;
      } catch (error) {
        this.delfosDevice = null;
      }
    },
    async persistDelfosPreferences(changes) {
      this.isSavingDelfos = true;
      this.delfosPreferences = {
        ...this.delfosPreferences,
        ...changes,
      };
      try {
        if (this.delfosPreferences.enabled && !hasPushPermissions()) {
          const enabled = await requestPushPermissions({
            onSuccess: this.onRegistrationSuccess,
          });
          if (!enabled) {
            this.delfosPreferences.enabled = false;
            return;
          }
        }
        await updateDelfosPushSubscription(this.delfosPreferences);
        this.hasEnabledPushPermissions =
          this.delfosPreferences.enabled && hasPushPermissions();
        await this.refreshDelfosDevice();
        window.dispatchEvent(new CustomEvent('delfos:notifications-updated'));
      } catch (error) {
        useAlert('Nao foi possivel salvar os avisos deste computador.');
      } finally {
        this.isSavingDelfos = false;
      }
    },
    setDelfosEnabled(value) {
      return this.persistDelfosPreferences({ enabled: value });
    },
    setDelfosOption(key, value) {
      return this.persistDelfosPreferences({ [key]: value });
    },
    setInternalChannelEnabled(channelId, enabled) {
      const mutedIds = new Set(
        this.delfosPreferences.mutedInternalChannelIds || []
      );
      if (enabled) mutedIds.delete(channelId);
      else mutedIds.add(channelId);
      return this.persistDelfosPreferences({
        mutedInternalChannelIds: [...mutedIds],
      });
    },
    isInternalChannelEnabled(channelId) {
      return !(this.delfosPreferences.mutedInternalChannelIds || []).includes(
        channelId
      );
    },
    muteDelfos(hours) {
      const mutedUntil = hours
        ? new Date(Date.now() + hours * 60 * 60 * 1000).toISOString()
        : null;
      return this.persistDelfosPreferences({ mutedUntil });
    },
    muteDelfosUntilEndOfDay() {
      const endOfDay = new Date();
      endOfDay.setHours(23, 59, 59, 999);
      return this.persistDelfosPreferences({
        mutedUntil: endOfDay.toISOString(),
      });
    },
    removeDelfosDevice() {
      return this.persistDelfosPreferences({ enabled: false });
    },
    async testDelfosNotification() {
      this.isTestingDelfos = true;
      try {
        const { data } = await DelfosNotificationsAPI.test(
          this.accountId,
          getDelfosDeviceId()
        );
        if (!data.delivered) throw new Error('Push was not delivered');
        useAlert('Notificacao de teste enviada ao Windows.');
      } catch (error) {
        useAlert('Nao foi possivel enviar a notificacao de teste.');
      } finally {
        this.isTestingDelfos = false;
      }
    },
    checkFlagStatus(type, flagType) {
      const selectedFlags =
        type === 'email' ? this.selectedEmailFlags : this.selectedPushFlags;
      return selectedFlags.includes(`${type}_${flagType}`);
    },
    onRegistrationSuccess() {
      this.hasEnabledPushPermissions = true;
      this.delfosPreferences = getDelfosNotificationPreferences();
      this.refreshDelfosDevice();
    },
    onRequestPermissions(value) {
      if (value) {
        // Enable / re-enable push notifications
        requestPushPermissions({
          onSuccess: this.onRegistrationSuccess,
        });
      } else {
        // Disable push notifications
        this.disablePushPermissions();
      }
    },
    async disablePushPermissions() {
      await unregisterDelfosPushSubscription({ preservePreference: false });
      this.delfosPreferences = getDelfosNotificationPreferences();
      this.hasEnabledPushPermissions = false;
      await this.refreshDelfosDevice();
    },
    getPushSubscription() {
      verifyServiceWorkerExistence(registration =>
        registration.pushManager
          .getSubscription()
          .then(subscription => {
            if (!subscription) {
              this.hasEnabledPushPermissions = false;
            } else {
              this.hasEnabledPushPermissions = true;
            }
          })
          // eslint-disable-next-line no-console
          .catch(error => console.log(error))
      );
    },
    async updateNotificationSettings() {
      try {
        this.$store.dispatch('userNotificationSettings/update', {
          selectedEmailFlags: this.selectedEmailFlags,
          selectedPushFlags: this.selectedPushFlags,
        });
        useAlert(this.$t('PROFILE_SETTINGS.FORM.API.UPDATE_SUCCESS'));
      } catch (error) {
        useAlert(this.$t('PROFILE_SETTINGS.FORM.API.UPDATE_ERROR'));
      }
    },
    handleInput(type, id) {
      if (type === 'email') {
        this.handleEmailInput(id);
      } else {
        this.handlePushInput(id);
      }
    },
    handleEmailInput(id) {
      this.selectedEmailFlags = this.toggleInput(this.selectedEmailFlags, id);
      this.updateNotificationSettings();
    },
    handlePushInput(id) {
      this.selectedPushFlags = this.toggleInput(this.selectedPushFlags, id);
      this.updateNotificationSettings();
    },
    toggleInput(selected, current) {
      if (selected.includes(current)) {
        const newSelectedFlags = selected.filter(flag => flag !== current);
        return newSelectedFlags;
      }
      return [...selected, current];
    },
  },
};
</script>

<template>
  <div id="profile-settings-notifications" class="flex flex-col gap-6">
    <section
      class="flex flex-col overflow-hidden border border-solid border-n-weak rounded-lg"
      aria-labelledby="delfos-notification-title"
    >
      <header
        class="flex flex-col gap-1 px-5 py-4 border-b border-n-weak sm:flex-row sm:items-center sm:justify-between"
      >
        <div class="min-w-0">
          <h3
            id="delfos-notification-title"
            class="text-heading-3 text-n-slate-12"
          >
            {{ delfosCopy.title }}
          </h3>
          <p class="text-body-small text-n-slate-10">
            {{ delfosPreferences.deviceName }}
          </p>
        </div>
        <div class="flex items-center gap-3">
          <span class="text-body-small text-n-slate-11">
            {{ delfosStatusLabel }}
          </span>
          <ToggleSwitch
            v-model="delfosPreferences.enabled"
            :disabled="isSavingDelfos"
            @change="setDelfosEnabled"
          />
        </div>
      </header>

      <div class="grid grid-cols-1 sm:grid-cols-2">
        <label
          class="flex items-center justify-between min-h-16 gap-4 px-5 py-3 border-b border-n-weak sm:border-r"
        >
          <span class="flex items-center gap-3 min-w-0">
            <span
              aria-hidden="true"
              class="i-lucide-volume-2 size-5 shrink-0"
            />
            <span class="text-body-main text-n-slate-12">
              {{ delfosCopy.sound }}
            </span>
          </span>
          <ToggleSwitch
            v-model="delfosPreferences.soundEnabled"
            :disabled="!delfosPreferences.enabled || isSavingDelfos"
            @change="value => setDelfosOption('soundEnabled', value)"
          />
        </label>

        <label
          class="flex items-center justify-between min-h-16 gap-4 px-5 py-3 border-b border-n-weak"
        >
          <span class="flex items-center gap-3 min-w-0">
            <span aria-hidden="true" class="i-lucide-eye size-5 shrink-0" />
            <span class="text-body-main text-n-slate-12">
              {{ delfosCopy.preview }}
            </span>
          </span>
          <ToggleSwitch
            v-model="delfosPreferences.previewEnabled"
            :disabled="!delfosPreferences.enabled || isSavingDelfos"
            @change="value => setDelfosOption('previewEnabled', value)"
          />
        </label>

        <label
          class="flex items-center justify-between min-h-16 gap-4 px-5 py-3 border-b border-n-weak sm:border-r"
        >
          <span class="flex items-center gap-3 min-w-0">
            <span
              aria-hidden="true"
              class="i-lucide-messages-square size-5 shrink-0"
            />
            <span class="text-body-main text-n-slate-12">
              {{ delfosCopy.internal }}
            </span>
          </span>
          <ToggleSwitch
            v-model="delfosPreferences.internalEnabled"
            :disabled="!delfosPreferences.enabled || isSavingDelfos"
            @change="value => setDelfosOption('internalEnabled', value)"
          />
        </label>

        <div
          class="flex items-center justify-between min-h-16 gap-4 px-5 py-3 border-b border-n-weak"
        >
          <span class="flex items-center gap-3 min-w-0">
            <span
              aria-hidden="true"
              class="i-lucide-bell-off size-5 shrink-0"
            />
            <span class="text-body-main text-n-slate-12">
              {{ isDelfosMuted ? 'Silenciado' : 'Recebendo avisos' }}
            </span>
          </span>
          <button
            v-if="isDelfosMuted"
            type="button"
            class="h-9 px-3 text-sm font-medium text-n-slate-12 border border-n-weak rounded-lg hover:bg-n-alpha-2"
            @click="muteDelfos(0)"
          >
            {{ delfosCopy.reactivate }}
          </button>
        </div>
      </div>

      <div
        v-if="delfosPreferences.internalEnabled"
        class="grid grid-cols-1 gap-2 px-5 py-4 border-b border-n-weak sm:grid-cols-2"
      >
        <label
          v-for="channel in [
            { id: 2, name: 'Equipe Instituto Delfos' },
            { id: 3, name: 'Recepcao - Instituto Delfos' },
          ]"
          :key="channel.id"
          class="flex items-center justify-between min-h-11 gap-3 px-3 border border-n-weak rounded-lg"
        >
          <span class="truncate text-body-small text-n-slate-12">
            {{ channel.name }}
          </span>
          <ToggleSwitch
            :model-value="isInternalChannelEnabled(channel.id)"
            :disabled="isSavingDelfos"
            @change="value => setInternalChannelEnabled(channel.id, value)"
          />
        </label>
      </div>

      <div
        class="flex flex-col gap-2 px-5 py-4 border-b border-n-weak sm:flex-row sm:items-center"
      >
        <label
          for="delfos-device-name"
          class="text-body-small text-n-slate-11 sm:w-44"
        >
          {{ delfosCopy.deviceName }}
        </label>
        <input
          id="delfos-device-name"
          v-model.trim="delfosPreferences.deviceName"
          type="text"
          maxlength="80"
          class="h-10 flex-1 px-3 text-body-main text-n-slate-12 bg-n-alpha-1 border border-n-weak rounded-lg"
          :disabled="isSavingDelfos"
          @change="setDelfosOption('deviceName', delfosPreferences.deviceName)"
        />
      </div>

      <footer class="flex flex-wrap items-center gap-2 px-5 py-4">
        <button
          type="button"
          class="flex items-center gap-2 h-10 px-3 text-sm font-medium text-n-slate-12 border border-n-weak rounded-lg hover:bg-n-alpha-2 disabled:opacity-50"
          :disabled="!delfosPreferences.enabled || isSavingDelfos"
          @click="muteDelfos(0.5)"
        >
          <span aria-hidden="true" class="i-lucide-clock-3 size-4 shrink-0" />
          {{ delfosCopy.mute30 }}
        </button>
        <button
          type="button"
          class="flex items-center gap-2 h-10 px-3 text-sm font-medium text-n-slate-12 border border-n-weak rounded-lg hover:bg-n-alpha-2 disabled:opacity-50"
          :disabled="!delfosPreferences.enabled || isSavingDelfos"
          @click="muteDelfos(1)"
        >
          <span aria-hidden="true" class="i-lucide-clock-3 size-4 shrink-0" />
          {{ delfosCopy.muteHour }}
        </button>
        <button
          type="button"
          class="flex items-center gap-2 h-10 px-3 text-sm font-medium text-n-slate-12 border border-n-weak rounded-lg hover:bg-n-alpha-2 disabled:opacity-50"
          :disabled="!delfosPreferences.enabled || isSavingDelfos"
          @click="muteDelfosUntilEndOfDay"
        >
          <span aria-hidden="true" class="i-lucide-clock-3 size-4 shrink-0" />
          {{ delfosCopy.muteDay }}
        </button>
        <button
          type="button"
          class="flex items-center gap-2 h-10 px-3 text-sm font-medium text-white bg-n-teal-10 rounded-lg hover:bg-n-teal-11 disabled:opacity-50 sm:ml-auto"
          :disabled="
            !delfosPreferences.enabled ||
            isTestingDelfos ||
            !delfosDevice?.registered
          "
          @click="testDelfosNotification"
        >
          <span aria-hidden="true" class="i-lucide-bell-ring size-4 shrink-0" />
          {{ isTestingDelfos ? delfosCopy.testing : delfosCopy.test }}
        </button>
        <button
          type="button"
          class="flex items-center justify-center size-10 text-n-ruby-9 border border-n-weak rounded-lg hover:bg-n-alpha-2 disabled:opacity-50"
          :title="delfosCopy.remove"
          :disabled="!delfosPreferences.enabled || isSavingDelfos"
          @click="removeDelfosDevice"
        >
          <span aria-hidden="true" class="i-lucide-trash-2 size-4 shrink-0" />
        </button>
      </footer>
    </section>

    <!-- Layout for desktop devices -->
    <div class="hidden sm:block">
      <div
        class="grid content-center h-12 grid-cols-12 gap-4 py-0 rounded-t-xl"
      >
        <TableHeaderCell
          :span="7"
          label="`${$t('PROFILE_SETTINGS.FORM.NOTIFICATIONS.TYPE_TITLE')}`"
        >
          <span class="text-heading-3 normal-case text-n-slate-12">
            {{ $t('PROFILE_SETTINGS.FORM.NOTIFICATIONS.TYPE_TITLE') }}
          </span>
        </TableHeaderCell>
        <TableHeaderCell
          :span="2"
          label="`${$t('PROFILE_SETTINGS.FORM.NOTIFICATIONS.EMAIL')}`"
        >
          <span class="text-heading-3 normal-case text-n-slate-12">
            {{ $t('PROFILE_SETTINGS.FORM.NOTIFICATIONS.EMAIL') }}
          </span>
        </TableHeaderCell>
        <TableHeaderCell
          :span="3"
          label="`${$t('PROFILE_SETTINGS.FORM.NOTIFICATIONS.PUSH')}`"
        >
          <div class="flex items-center justify-between gap-1">
            <span
              class="text-heading-3 normal-case text-n-slate-12 whitespace-nowrap"
            >
              {{ $t('PROFILE_SETTINGS.FORM.NOTIFICATIONS.PUSH') }}
            </span>
          </div>
        </TableHeaderCell>
      </div>
      <div
        v-for="(notification, index) in filteredNotificationTypes"
        :key="index"
      >
        <div
          class="grid items-center content-center h-12 grid-cols-12 gap-4 py-0 rounded-t-xl"
        >
          <div
            class="flex flex-row items-start gap-2 col-span-7 px-0 py-2 text-sm tracking-[0.5] rtl:text-right"
          >
            <span class="text-body-main text-n-slate-12">
              {{ $t(notification.label) }}
            </span>
          </div>
          <div
            v-for="(type, typeIndex) in ['email', 'push']"
            :key="typeIndex"
            class="flex items-start gap-2 px-0 text-sm tracking-[0.5] text-left rtl:text-right"
            :class="`col-span-${type === 'push' ? 3 : 2}`"
          >
            <CheckBox
              :value="`${type}_${notification.value}`"
              :is-checked="
                checkFlagStatus(type, notification.value, selectedPushFlags)
              "
              @update="id => handleInput(type, id)"
            />
          </div>
        </div>
      </div>
    </div>
    <!--  Layout for mobile devices -->
    <div class="flex flex-col gap-6 sm:hidden">
      <span class="text-heading-3 text-n-slate-12">
        {{ $t('PROFILE_SETTINGS.FORM.EMAIL_NOTIFICATIONS_SECTION.TITLE') }}
      </span>
      <div class="flex flex-col gap-4">
        <div
          v-for="(notification, index) in filteredNotificationTypes"
          :key="index"
          class="flex flex-row items-start gap-2"
        >
          <CheckBox
            :id="`email_${notification.value}`"
            :value="`email_${notification.value}`"
            :is-checked="checkFlagStatus('email', notification.value)"
            @update="handleEmailInput"
          />
          <span class="text-body-main text-n-slate-12">{{
            $t(notification.label)
          }}</span>
        </div>
      </div>

      <div class="flex items-center justify-start gap-2">
        <span class="text-heading-3 text-n-slate-12">
          {{ $t('PROFILE_SETTINGS.FORM.PUSH_NOTIFICATIONS_SECTION.TITLE') }}
        </span>
      </div>

      <div class="flex flex-col gap-4">
        <div
          v-for="(notification, index) in filteredNotificationTypes"
          :key="index"
          class="flex flex-row items-start gap-2"
        >
          <CheckBox
            :id="`push_${notification.value}`"
            :value="`push_${notification.value}`"
            :is-checked="checkFlagStatus('push', notification.value)"
            @update="handlePushInput"
          />
          <span class="text-body-main text-n-slate-12">{{
            $t(notification.label)
          }}</span>
        </div>
      </div>
    </div>

    <div
      class="flex items-center justify-between w-full gap-2 p-4 border border-solid border-n-weak rounded-xl"
    >
      <div class="flex flex-row items-center gap-2">
        <fluent-icon
          icon="alert"
          class="flex-shrink-0 text-n-slate-12"
          size="18"
        />
        <span class="text-body-main text-n-slate-12">
          {{ $t('PROFILE_SETTINGS.FORM.NOTIFICATIONS.BROWSER_PERMISSION') }}
        </span>
      </div>
      <ToggleSwitch
        v-model="hasEnabledPushPermissions"
        @change="onRequestPermissions"
      />
    </div>
  </div>
</template>
